import { Alert, AlertDescription, AlertTitle } from '@neo/primitives/base/alert'
import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { AlertCircle } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ButtonWithPermission } from 'v2/shared/components/button-with-permission'
import type { CustomField } from 'v2/shared/services/external-apps-service'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import {
  useCustomFieldsQuery,
  useResyncCustomFieldsMutation,
  useUpdateCustomFieldsMutation,
} from '../queries'
import { IntegrationState } from '../utils/constants'
import { EntityFieldsSection } from './entity-fields-section'

interface CustomFieldsDialogProps {
  app: RExternalOrgApp
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const CustomFieldsDialog = ({ app, open, onOpenChange }: CustomFieldsDialogProps) => {
  const [fieldsByEntity, setFieldsByEntity] = useState<Record<string, CustomField[]>>({})
  const entities = app.custom_field_entity || []
  const isCustomFieldsEnabled = app.is_custom_fields_enabled

  const { data: batchFields = {}, isLoading: isBatchLoading } = useCustomFieldsQuery(
    app?.id,
    isCustomFieldsEnabled,
    entities,
    app.state === IntegrationState.ACTIVE && open,
  )

  const { mutateAsync: resyncCustomFields, isPending: isResyncingCustomFields } =
    useResyncCustomFieldsMutation()

  const { mutateAsync: updateCustomFields, isPending: isSaving } = useUpdateCustomFieldsMutation()

  const handleSyncCustomFields = async () => {
    try {
      const result = await resyncCustomFields({
        appId: app.id,
        entities,
      })
      if (result?.fields_by_entity) {
        setFieldsByEntity(result.fields_by_entity)
      }
    } catch {
      // Error toast is handled by mutation's onError
    }
  }

  const isInitialized = useRef(false)

  useEffect(() => {
    if (Object.keys(batchFields).length > 0 && !isInitialized.current) {
      isInitialized.current = true
      setFieldsByEntity(batchFields)
    }
  }, [batchFields])

  // Reset initialization when dialog closes
  useEffect(() => {
    if (!open) {
      isInitialized.current = false
    }
  }, [open])

  const handleEntityFieldsChange = useCallback((entity: string, fields: CustomField[]) => {
    setFieldsByEntity((prev) => ({ ...prev, [entity]: fields }))
  }, [])

  const handleSubmit = async () => {
    const entitiesToSave = Object.fromEntries(
      Object.entries(fieldsByEntity).filter(([_, fields]) => fields.length > 0),
    )

    if (Object.keys(entitiesToSave).length === 0) return

    try {
      await updateCustomFields({
        appId: app.id,
        fieldsByEntity: entitiesToSave,
      })
      setFieldsByEntity({})
      onOpenChange(false)
    } catch {
      // Error toast is handled by mutation's onError
      // Keep dialog open so user can retry
    }
  }

  const handleCancel = () => {
    setFieldsByEntity({})
    onOpenChange(false)
  }

  const hasFieldsToSave = entities.some((entity) => fieldsByEntity[entity]?.length > 0)

  return (
    // Prevent accidental dialog closure - only allow explicit Cancel/Save actions
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          {isCustomFieldsEnabled ? (
            <div className="flex justify-between items-start">
              <div>
                <DialogTitle>{app.name} Custom Fields</DialogTitle>
                <p className="text-p-400 text-secondary mt-2">
                  Configure custom fields for {entities.join(', ')} integration.
                </p>
              </div>
              <ButtonWithPermission
                onClick={handleSyncCustomFields}
                variant="outline"
                size="sm"
                loading={isResyncingCustomFields}
                disabled={isResyncingCustomFields || app.state !== IntegrationState.ACTIVE}
                disabledReason={
                  app.state !== IntegrationState.ACTIVE
                    ? 'Integration must be active to sync custom fields'
                    : undefined
                }
                data-testid="button-sync-custom-fields"
                module={RbacModules.integration}
                permissionType={RbacPermissionTypes.edit}
              >
                Sync
              </ButtonWithPermission>
            </div>
          ) : (
            <DialogTitle>{app.name} Custom Fields</DialogTitle>
          )}
        </DialogHeader>

        {isCustomFieldsEnabled ? (
          <ScrollArea className="max-h-[400px]">
            {entities.map((entity, index) => (
              <EntityFieldsSection
                key={entity}
                entity={entity}
                fields={fieldsByEntity[entity] || []}
                isLoading={isBatchLoading}
                onFieldsChange={handleEntityFieldsChange}
                defaultOpen={index === 0}
              />
            ))}
          </ScrollArea>
        ) : (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Custom Fields Not Enabled</AlertTitle>
            <AlertDescription>
              Custom fields are not enabled for this integration. Please contact support to enable
              this feature.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter className="flex sm:justify-between">
          <Button
            variant="outline"
            type="button"
            onClick={handleCancel}
            data-testid="button-cancel-custom-fields"
          >
            Cancel
          </Button>
          {isCustomFieldsEnabled && (
            <ButtonWithPermission
              onClick={handleSubmit}
              disabled={isSaving || !hasFieldsToSave}
              module={RbacModules.integration}
              permissionType={RbacPermissionTypes.edit}
              disabledReason={!hasFieldsToSave ? 'No custom fields available to save' : undefined}
              data-testid="button-save-custom-fields"
            >
              {isSaving ? (
                <div className="flex items-center gap-2">
                  <span className="loading loading-spinner loading-sm" />
                  Saving changes...
                </div>
              ) : (
                'Save changes'
              )}
            </ButtonWithPermission>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
